// src/receptionist/components/SetupBanner.tsx
//
// "Setup is not finished", above every merchant screen (ourteam-frontend#4789).
//
// The Setup checklist lives on Business profile only, so a merchant working in
// Bookings never learnt that templates were not approved, which is what lets a
// confirmation or a reminder go out after WhatsApp's 24-hour window. This reads
// the same checklist (GET .../whatsapp) and names the steps still open.
//
// Closing it hides it for 14 days for that merchant, kept in localStorage. A
// browser that refuses storage still hides it for the visit. Nothing shows
// while the state loads, when it fails, or once every step is done.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangleIcon, XIcon } from "lucide-react";
import { getWhatsappState, type WhatsappState } from "@service/receptionist";
import { FOCUS_RING, TYPE } from "@oura/ui";
import { cn } from "@oura/lib/cn";
import { SETUP_CHECKLIST } from "./WhatsappOnboarding";

const HIDE_FOR_MS = 14 * 24 * 60 * 60 * 1000;
/** A fresh read per screen is wasteful; a minute old is fresh enough for a reminder. */
const CACHE_MS = 60 * 1000;
const cache = new Map<string, { at: number; checklist: WhatsappState["checklist"] }>();

/** Fired when a setup step changes here (a sign-off, a template submit), so the Setup page re-reads it. */
export const SETUP_CHANGED_EVENT = "rcp:setup-changed";

/** After a step changes here (a sign-off), the next screen reads it fresh. */
export function forgetSetupState(merchantId: string): void {
    cache.delete(merchantId);
    window.dispatchEvent(new CustomEvent(SETUP_CHANGED_EVENT, { detail: { id: merchantId } }));
}

const storageKey = (merchantId: string) => `rcp.setupBanner.hiddenAt.${merchantId}`;

function hiddenNow(merchantId: string): boolean {
    try {
        const raw = window.localStorage.getItem(storageKey(merchantId));
        const at = raw ? Date.parse(raw) : NaN;
        return Number.isFinite(at) && Date.now() - at < HIDE_FOR_MS;
    } catch {
        return false;
    }
}

export function SetupBanner({
    merchantId,
    setupHref,
    onSetupPage,
}: {
    /** The merchant on screen: the URL's for staff, the login's own for a client. */
    merchantId: string;
    /** Business profile, at its Setup card. */
    setupHref: string;
    /** Business profile itself: the card is on the page, so no link to it. */
    onSetupPage: boolean;
}) {
    const [checklist, setChecklist] = useState<WhatsappState["checklist"] | null>(null);
    const [hidden, setHidden] = useState(() => hiddenNow(merchantId));

    useEffect(() => {
        setHidden(hiddenNow(merchantId));
        setChecklist(null);
        if (hiddenNow(merchantId)) return;
        const cached = cache.get(merchantId);
        if (cached && Date.now() - cached.at < CACHE_MS) {
            setChecklist(cached.checklist);
            return;
        }
        let live = true;
        getWhatsappState(merchantId)
            .then((s) => {
                cache.set(merchantId, { at: Date.now(), checklist: s.checklist });
                if (live) setChecklist(s.checklist);
            })
            // A banner is a reminder: a failed read shows nothing rather than an error on every screen.
            .catch(() => undefined);
        return () => {
            live = false;
        };
    }, [merchantId]);

    if (hidden || !checklist) return null;
    const open = SETUP_CHECKLIST.filter(({ key }) => !checklist[key]);
    if (open.length === 0) return null;

    const hide = () => {
        setHidden(true);
        try {
            window.localStorage.setItem(storageKey(merchantId), new Date().toISOString());
        } catch {
            /* hidden for this visit; nothing more to do */
        }
    };

    return (
        <div
            role="status"
            className="mb-5 flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200"
        >
            <AlertTriangleIcon aria-hidden size={16} className="mt-0.5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
                <p className={cn(TYPE.tc, "m-0 font-semibold text-amber-900")}>Setup is not finished</p>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-amber-800")}>
                    {open.length} of {SETUP_CHECKLIST.length} {open.length === 1 ? "step" : "steps"} left: {open.map((o) => o.label).join(", ")}.
                    {!onSetupPage && (
                        <>
                            {" "}
                            <Link to={setupHref} className={cn("rounded font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950", FOCUS_RING)}>
                                Finish setup
                            </Link>
                        </>
                    )}
                </p>
            </div>
            <button
                type="button"
                onClick={hide}
                aria-label="Hide for 14 days"
                title="Hide for 14 days"
                className={cn("-m-1 grid h-7 w-7 shrink-0 place-items-center rounded-full text-amber-700 hover:bg-amber-100", FOCUS_RING)}
            >
                <XIcon aria-hidden size={14} />
            </button>
        </div>
    );
}
